import { useCallback, useEffect, useState } from "react";

import { sensitive } from "../lib/sensitive";
import { useScrollLock } from "../useScrollLock";
import { PageHead } from "./Shell";
import { FilePreview } from "./FilePreview";
import {
  AdminError, admin, saveFile,
  type AuditRow, type RecordRow, type StoredFileRow,
} from "./api";

/**
 * Odpowiedź w teczce — z tym samym zestawem wariantów i regułami, które widział
 * kandydat. Panel poprawia ją dokładnie takim polem, jakim ona powstała:
 * lista wyborem z listy, data kalendarzem, plik plikiem.
 */
export interface AnsweredField {
  key: string;
  type: string;
  title: string;
  display: string;
  origin: string;
  value?: string;
  hint?: string | null;
  placeholder?: string | null;
  optional?: boolean;
  options?: { id: string; label: string }[];
  validation?: { regex?: string | null; checksum?: string | null; mask?: string | null };
  title_candidate?: string;
  display_candidate?: string;
  /** Pytanie plikowe, z którego OCR odczytał tę wartość — do podglądu źródła. */
  ocr_source?: string;
  /** Faktyczne źródło autouzupełnienia: `idenfy` | `geocode` | `ocr`. */
  source?: string;
  /** Pytanie iDenfy: odczyty ze skanu (i dokąd trafiły) oraz pliki dokumentu. */
  values?: KycValue[];
}

export type KycValue =
  | { kind: "reading"; field: string; label: string; value: string; target: string | null }
  | { kind: "file"; file_id: string; filename: string; content_type: string };

/** Co pokazujemy w podglądzie — null zamyka okno. */
type Preview = { path: string; title: string; filename: string; init?: RequestInit } | null;

/**
 * Trzy osie, na których stoi sprawa — każda odpowiada na inne pytanie.
 * Wcześniej mieszały się w jednej plakietce i wychodziły z tego zdania bez
 * sensu: „zakończona" sprawa, która czeka na uzupełnienie.
 */
const MASTER_LABEL: Record<string, string> = {
  not_started: "Nierozpoczęty",
  in_progress: "W trakcie",
  completed: "Zakończony",
  archived: "Zarchiwizowany",
};

const HELP_LABEL: Record<string, string> = {
  none: "Nie jest potrzebna",
  requested: "Prosi o pomoc",
  provided: "Pomoc udzielona",
};

/** Język kandydata po ludzku — kod „uk" nic nie mówi przez telefon. */
const JEZYK: Record<string, string> = {
  pl: "polski",
  en: "angielski",
  ru: "rosyjski",
  uk: "ukraiński",
};

/** Skąd wzięła się wartość. „Konsultant" nie może wyglądać jak odczyt z dokumentu. */
const ORIGIN_BADGE: Record<string, string> = {
  verified: "KYC",
  system: "auto",
  consultant: "konsultant",
};

// Faktyczne źródło autouzupełnienia — konsultant widzi, skąd wartość, zamiast
// jednego „KYC" dla wszystkiego (iDenfy, adres z Google, odczyt Gemini OCR).
const SOURCE_BADGE: Record<string, string> = {
  idenfy: "iDenfy",
  geocode: "Google Maps",
  ocr: "Gemini OCR",
};

/**
 * Kto stoi za wpisem w dzienniku. Kolejność wyjaśnia różnicę, która ma tu
 * największe znaczenie: „kandydat tak napisał" to zupełnie co innego niż
 * „konsultant tak zmienił".
 */
const SOURCE_LABEL: Record<string, string> = {
  candidate: "kandydat",
  consultant: "konsultant",
  api: "API",
  document: "z dokumentu",
  system: "automat",
};

const HELP_STATES = [
  { id: "none", label: "Nie jest potrzebna" },
  { id: "requested", label: "Prosi o pomoc" },
  { id: "provided", label: "Pomoc udzielona" },
] as const;

/** Lista procesów z filtrem i wyszukiwarką po numerze telefonu albo ID. */
export function Records({
  onOpen,
  search: external,
  schemas = [],
}: {
  onOpen: (id: string) => void;
  search?: string;
  schemas?: string[];
}) {
  const [rows, setRows] = useState<RecordRow[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [status, setStatus] = useState("");
  const [schemaId, setSchemaId] = useState("");
  const [helpFilter, setHelpFilter] = useState("");
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [preview, setPreview] = useState<Preview>(null);
  const [limit, setLimit] = useState(25);
  const [jump, setJump] = useState("");

  // Wyszukiwarka w menu bocznym steruje tą samą listą.
  useEffect(() => {
    if (external !== undefined) {
      setSearch(external);
      setOffset(0);
    }
  }, [external]);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const data = await admin.records({
        master: status || undefined,
        search: search || undefined,
        schemaId: schemaId || undefined,
        helpStatus: helpFilter || undefined,
        limit,
        offset,
      });
      setRows(data.items);
      setTotal(data.total);
      setError(null);
    } catch (caught) {
      setError((caught as AdminError).message);
    } finally {
      setBusy(false);
    }
  }, [status, search, schemaId, helpFilter, offset, limit]);

  useEffect(() => {
    const timer = window.setTimeout(load, search ? 250 : 0);
    return () => window.clearTimeout(timer);
  }, [load, search]);

  const toggle = (id: string) =>
    setPicked((current) => {
      const next = new Set(current);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const runBulk = async (action: "close" | "reset" | "archive") => {
    const labels = { close: "zamknąć", reset: "zresetować", archive: "zarchiwizować" };
    if (!window.confirm(`Na pewno ${labels[action]} zaznaczone procesy (${picked.size})?`)) return;
    try {
      await admin.bulk([...picked], action);
      setPicked(new Set());
      await load();
    } catch (caught) {
      setError((caught as AdminError).message);
    }
  };

  const pages = Math.max(1, Math.ceil(total / limit));
  const page = Math.floor(offset / limit) + 1;

  return (
    <section className="adm__section">
      <PageHead
        title="Procesy"
        // „Tenant" to słowo z bazy danych, nie z pracy konsultanta.
        subtitle={`${total} kandydatów u tego zleceniodawcy`}
        actions={
          <button
            className="btn btn--secondary"
            onClick={() =>
              saveFile("/api/admin/records?limit=200", "procesy.json").catch((caught) =>
                setError((caught as AdminError).message),
              )
            }
          >
            Eksport JSON
          </button>
        }
      />

      <div className="adm__toolbar">
        <input
          className="input"
          type="search"
          placeholder="Szukaj po telefonie, numerze sprawy albo ID"
          value={search}
          onChange={(event) => {
            setOffset(0);
            setSearch(event.target.value);
          }}
        />
        <select
          className="select"
          value={status}
          onChange={(event) => {
            setOffset(0);
            setStatus(event.target.value);
          }}
        >
          <option value="">Każdy stan procesu</option>
          {Object.entries(MASTER_LABEL).map(([id, label]) => (
            <option key={id} value={id}>
              {label}
            </option>
          ))}
        </select>
        <select
          className="select"
          value={helpFilter}
          onChange={(event) => {
            setOffset(0);
            setHelpFilter(event.target.value);
          }}
        >
          <option value="">Każdy stan pomocy</option>
          {HELP_STATES.map((state) => (
            <option key={state.id} value={state.id}>
              {state.label}
            </option>
          ))}
        </select>
        {/* Filtr pojawia się dopiero przy drugim procesie — przy jednym byłby
            listą z jedną pozycją, czyli ozdobą zajmującą miejsce. */}
        {schemas.length > 1 && (
          <select
            className="select"
            value={schemaId}
            onChange={(event) => {
              setOffset(0);
              setSchemaId(event.target.value);
            }}
          >
            <option value="">Wszystkie procesy</option>
            {schemas.map((id) => (
              <option key={id} value={id}>
                {id}
              </option>
            ))}
          </select>
        )}
        {/* Jedyna kolejka, którą warto obsłużyć od razu: człowiek czeka i wie,
            że czeka — sam się zgłosił. Skrót do tego samego, co filtr obok. */}
        <button
          className={`btn ${helpFilter === "requested" ? "btn--primary" : "btn--ghost"}`}
          onClick={() => {
            setOffset(0);
            setHelpFilter((value) => (value === "requested" ? "" : "requested"));
          }}
        >
          {helpFilter === "requested" ? "Wszystkie" : "Proszą o pomoc"}
        </button>
        <span className="adm__count">{total} procesów</span>
      </div>

      {error && <p className="error">{error}</p>}

      {/* Podczas dociągania danych (filtr, strona, szukanie) stare wiersze
          przygasają — modyfikator tylko steruje kryciem w CSS. */}
      <div className={`adm__tablewrap${busy ? " adm__tablewrap--busy" : ""}`}>
        <table className="adm__table">
          <thead>
            <tr>
              <th className="adm__pick">
                {/* Etykieta wokół checkboxa rozszerza cel dotykowy do 44 px —
                    sam kwadracik ma 18 px i w biegu się w niego nie trafia. */}
                <label className="adm__pickbox">
                  <input
                    type="checkbox"
                    aria-label="Zaznacz wszystkie"
                    checked={rows.length > 0 && picked.size === rows.length}
                    onChange={(event) =>
                      setPicked(event.target.checked ? new Set(rows.map((r) => r.record_id)) : new Set())
                    }
                  />
                </label>
              </th>
              <th>Kandydat</th>
              <th>Telefon</th>
              {schemas.length > 1 && <th>Proces</th>}
              {/* Trzy osie, trzy kolumny: co robi kandydat, czy prosił o kontakt,
                  czy teczkę trzeba obejrzeć. W jednej plakietce zlewały się
                  w zdania bez sensu. Kolumna nazywa się „Stan", bo przy dwóch
                  procesach obok stała druga kolumna „Proces" i nagłówki się
                  dublowały. */}
              <th>Stan</th>
              <th>Pomoc</th>
              <th>Postęp</th>
              {/* Liczby równane do prawej — kolumna cyfr czyta się pionem. */}
              <th className="adm__num">Odpowiedzi</th>
              <th>Aktualizacja</th>
              <th />
            </tr>
          </thead>
          {/* Kaskada z tokens.css: wiersze wchodzą jeden po drugim po zmianie
              filtra albo strony; --i to indeks opóźnienia, sufit trzyma
              --stagger-max, a reduced-motion gasi ruch do samego krycia. */}
          <tbody className="kaskada">
            {rows.map((row, index) => (
              <tr
                key={row.record_id}
                style={{ "--i": index } as React.CSSProperties}
                className={picked.has(row.record_id) ? "adm__row--picked" : undefined}
                onClick={(event) => {
                  // React słucha zdarzeń na korzeniu, więc stopPropagation na komórce
                  // zabiłby też onChange checkboxa. Zamiast tego pytamy, gdzie kliknięto.
                  if ((event.target as HTMLElement).closest(".adm__pick")) return;
                  onOpen(row.record_id);
                }}
              >
                <td className="adm__pick">
                  <label className="adm__pickbox">
                    <input
                      type="checkbox"
                      aria-label={`Zaznacz ${row.phone}`}
                      checked={picked.has(row.record_id)}
                      onChange={() => toggle(row.record_id)}
                    />
                  </label>
                </td>
                <td>
                  <span className="adm__name">{row.name ?? "—"}</span>
                  {row.verified && <span className="badge badge--verified">KYC</span>}
                  {/* Numer sprawy u zlecającego: konsultant dostaje telefon
                      z tym numerem, a nie z naszym UUID-em. */}
                  {row.external_id && <span className="adm__case">{row.external_id}</span>}
                </td>
                <td className="adm__mono">{row.phone}</td>
                {schemas.length > 1 && (
                  <td>
                    <span className="adm__tag">{row.schema_id}</span>
                  </td>
                )}
                <td>
                  <span className={`pill pill--${row.master}`}>
                    {row.master_label || MASTER_LABEL[row.master] || row.master}
                  </span>
                </td>
                <td>
                  {/* Cisza jest tu normą, więc „nie jest potrzebna" nie zajmuje
                      miejsca — pusta komórka mówi to samo, nie krzycząc. */}
                  {row.help_status !== "none" && (
                    <span
                      className={`pill pill--help-${row.help_status}`}
                      title={formatDate(row.help_requested_at)}
                    >
                      {row.help_label || HELP_LABEL[row.help_status]}
                    </span>
                  )}
                </td>
                <td className="adm__progress">
                  {/* Pasek pokazuje kształt, liczba wartość — sam pasek przy
                      szerokości komórki nie pozwalał odróżnić 60% od 80%. */}
                  <div className="meter">
                    <span style={{ width: `${row.progress}%` }} />
                  </div>
                  <span className="adm__pct">{row.progress}%</span>
                </td>
                <td className="adm__mono adm__num">{row.answers}</td>
                <td className="adm__muted adm__date">{formatDate(row.updated_at)}</td>
                {/* Strzałka w spanie, żeby hover mógł ją dosunąć transformem —
                    transform na samej komórce szarpie liniami tabeli. */}
                <td className="adm__chev">
                  <span aria-hidden="true">›</span>
                </td>
              </tr>
            ))}
            {/* Pierwsze ładowanie: szkielet wierszy zamiast pustej karty —
                układ tabeli stoi od razu, a połysk mówi „zaraz będą dane",
                zanim cokolwiek przyjdzie z serwera. */}
            {busy &&
              rows.length === 0 &&
              [0, 1, 2, 3, 4, 5].map((slot) => (
                <tr key={`skel-${slot}`} className="adm__skelrow" aria-hidden="true">
                  <td colSpan={schemas.length > 1 ? 11 : 10}>
                    <span className="skel" />
                  </td>
                </tr>
              ))}
            {!busy && rows.length === 0 && (
              <tr>
                <td colSpan={schemas.length > 1 ? 11 : 10} className="adm__empty">
                  {/* Pusta lista musi mówić, co dalej — inaczej wygląda jak
                      awaria, a nie jak wynik zbyt ciasnych filtrów. */}
                  <strong>Nic nie pasuje do filtrów</strong>
                  <span>Zmień stan albo wyczyść wyszukiwanie — sprawy nie znikają, tylko się chowają.</span>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="pager">
        <label className="pager__size">
          Na stronie
          <select
            className="select"
            value={limit}
            onChange={(event) => {
              setOffset(0);
              setLimit(Number(event.target.value));
            }}
          >
            {[10, 25, 50, 100].map((size) => (
              <option key={size}>{size}</option>
            ))}
          </select>
        </label>

        <div className="pager__pages">
          <button className="pager__btn" disabled={page === 1} onClick={() => setOffset(0)}>
            «
          </button>
          <button className="pager__btn" disabled={page === 1} onClick={() => setOffset(offset - limit)}>
            ‹
          </button>
          {pageNumbers(page, pages).map((item, index) =>
            item === "…" ? (
              <span key={`gap-${index}`} className="pager__gap">
                …
              </span>
            ) : (
              <button
                key={item}
                className={`pager__btn${item === page ? " pager__btn--on" : ""}`}
                onClick={() => setOffset((item - 1) * limit)}
              >
                {item}
              </button>
            ),
          )}
          <button className="pager__btn" disabled={page === pages} onClick={() => setOffset(offset + limit)}>
            ›
          </button>
          <button className="pager__btn" disabled={page === pages} onClick={() => setOffset((pages - 1) * limit)}>
            »
          </button>
        </div>

        <form
          className="pager__jump"
          onSubmit={(event) => {
            event.preventDefault();
            const target = Math.min(pages, Math.max(1, Number(jump) || 1));
            setOffset((target - 1) * limit);
            setJump("");
          }}
        >
          Przejdź do
          <input className="input" value={jump} onChange={(event) => setJump(event.target.value)} inputMode="numeric" />
          <button className="btn btn--secondary" type="submit">
            Idź
          </button>
        </form>
      </div>

      {preview && <FilePreview {...preview} onClose={() => setPreview(null)} />}

      {picked.size > 0 && (
        <div className="bulk" role="toolbar" aria-label="Akcje na zaznaczonych">
          <span className="bulk__count">{picked.size} zaznaczonych</span>
          <button
            className="bulk__action"
            onClick={() =>
              setPreview({
                path: "/api/admin/records/dossier.pdf",
                title: `Karty kandydatów (${picked.size})`,
                filename: `karty-kandydatow-${new Date().toISOString().slice(0, 10)}.pdf`,
                init: { method: "POST", body: JSON.stringify({ ids: [...picked] }) },
              })
            }
          >
            Karty PDF
          </button>
          <button className="bulk__action" onClick={() => runBulk("close")}>
            Zamknij
          </button>
          <button className="bulk__action" onClick={() => runBulk("reset")}>
            Zresetuj
          </button>
          <button className="bulk__action bulk__action--muted" onClick={() => runBulk("archive")}>
            Archiwizuj
          </button>
          <button className="bulk__close" onClick={() => setPicked(new Set())} aria-label="Odznacz wszystkie">
            ×
          </button>
        </div>
      )}
    </section>
  );
}

/** 1 … 4 5 6 … 25 — skraca listę stron, zostawiając sąsiadów bieżącej. */
function pageNumbers(page: number, pages: number): (number | "…")[] {
  if (pages <= 7) return Array.from({ length: pages }, (_, index) => index + 1);
  const result: (number | "…")[] = [1];
  const from = Math.max(2, page - 1);
  const to = Math.min(pages - 1, page + 1);
  if (from > 2) result.push("…");
  for (let index = from; index <= to; index++) result.push(index);
  if (to < pages - 1) result.push("…");
  result.push(pages);
  return result;
}

/** Akta jednego procesu: dane, ślad algorytmu, pliki, historia i akcje. */
export function RecordDetail({ id, onBack }: { id: string; onBack: () => void }) {
  const [data, setData] = useState<Record<string, any> | null>(null);
  const [audit, setAudit] = useState<AuditRow[]>([]);
  const [tab, setTab] = useState<"dane" | "algorytm" | "pliki" | "historia">("dane");
  const [preview, setPreview] = useState<Preview>(null);
  const [fixing, setFixing] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [detail, events] = await Promise.all([admin.record(id), admin.audit(id)]);
      setData(detail);
      setAudit(events);
      setError(null);
    } catch (caught) {
      setError((caught as AdminError).message);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Akcje konsultanta na sprawie.
   *
   * Każda kończy się pytaniem, bo każda jest widoczna dla kandydata: zamknięcie
   * odbiera mu dalszą drogę, przypomnienie wysyła SMS, reset kasuje wszystko,
   * co wpisał. Zgoda „na wszelki wypadek" jest tu tańsza niż cofanie.
   */
  /**
   * Poprawka numeru z CRM-u, który przyszedł z literówką: kod SMS szedł na cudzy
   * numer i kandydat nie mógł wejść. Kody wysłane na stary numer przestają działać.
   */
  const zmienTelefon = async () => {
    const numer = window.prompt("Nowy numer telefonu kandydata:", data?.phone ?? "");
    if (!numer?.trim() || numer.trim() === data?.phone) return;
    const zapros = (data?.record_status ?? data?.status) === "open"
      && window.confirm("Wysłać kandydatowi link SMS-em na nowy numer?");
    setBusy(true);
    try {
      const wynik = await admin.changePhone(id, numer.trim(), zapros);
      if (wynik.invited && wynik.invited.sent === false) {
        window.alert("Numer zmieniony, ale SMS nie wyszedł — sprawdź dziennik wysyłek.");
      }
      await load();
    } catch (caught) {
      setError((caught as AdminError).message);
    } finally {
      setBusy(false);
    }
  };

  const act = async (
    action: "success" | "failed" | "skip" | "remind" | "reset",
  ) => {
    const question = {
      success: "Zamknąć sprawę jako załatwioną?",
      failed: "Zamknąć sprawę jako odmowę? Kandydat nie będzie mógł jej kontynuować.",
      skip: "Pominąć pytanie, na którym stoi proces? Zapisze się jako „nie dotyczy”.",
      remind: "Wysłać kandydatowi SMS z jego linkiem?",
      reset: "Skasować wszystkie odpowiedzi i zacząć proces od nowa?",
    }[action];
    if (!window.confirm(question)) return;

    setBusy(true);
    try {
      const wynik = await {
        success: () => admin.close(id, "success"),
        failed: () => admin.close(id, "failed"),
        skip: () => admin.skip(id),
        remind: () => admin.remind(id),
        reset: () => admin.reset(id),
      }[action]();

      // Wysyłka SMS-a to jedyna akcja, której skutek dzieje się poza panelem —
      // „wysłane" musi znaczyć, że bramka to przyjęła, a nie że kliknięto.
      const wyslane = (wynik as { sent?: boolean }) ?? {};
      const dostarczone = wyslane.sent;
      if (dostarczone === false) {
        window.alert("SMS nie wyszedł — sprawdź dziennik wysyłek.");
      }
      await load();
    } catch (caught) {
      setError((caught as AdminError).message);
    } finally {
      setBusy(false);
    }
  };

  /**
   * Kasowanie odpowiedzi prosto z wiersza.
   *
   * Powód pytamy od razu, zamiast otwierać okno edycji: kto chce cofnąć błędną
   * odpowiedź, ma jedno kliknięcie i jedno zdanie do wpisania. Bez powodu nie
   * kasujemy — po skasowaniu cudzej odpowiedzi zostaje sama pustka, nie do
   * odróżnienia od utraty danych.
   */
  const czysc = async (key: string, tytul: string) => {
    const powod = window.prompt(`Skasować odpowiedź „${tytul}"? Podaj powód:`);
    if (!powod?.trim()) return;

    setBusy(true);
    try {
      const wynik = await admin.clearAnswer(id, key, powod.trim());
      // Kasowanie u korzenia gałęzi zabiera ze sobą wszystko, co z niej
      // wynikało — konsultant ma to zobaczyć od razu, a nie odkryć później.
      const dodatkowe = (wynik.cleared ?? []).filter((k) => k !== key);
      if (dodatkowe.length) {
        window.alert(
          `Razem z tą odpowiedzią zniknęły zależne od niej (${dodatkowe.length}):\n` +
            dodatkowe.join(", "),
        );
      }
      await load();
    } catch (caught) {
      setError((caught as AdminError).message);
    } finally {
      setBusy(false);
    }
  };

  const setHelp = async (state: string) => {
    setBusy(true);
    try {
      await admin.setHelp(id, state);
      await load();
    } catch (caught) {
      setError((caught as AdminError).message);
    } finally {
      setBusy(false);
    }
  };

  // Błąd w rytmie strony, nie goły akapit przyklejony do krawędzi.
  if (error) {
    return (
      <section className="adm__section">
        <p className="error">{error}</p>
      </section>
    );
  }
  // Szkielet akt zamiast kółka na pustym ekranie: nagłówek, pasek stanu
  // i teczka stoją od razu tam, gdzie za chwilę będą naprawdę — wzrok nie
  // zaczyna czytania od zera, gdy przyjdą dane.
  if (!data) {
    return (
      <section className="adm__section" aria-busy="true">
        <div className="adm__head" aria-hidden="true">
          <div className="adm__headmain">
            <span className="skel skel--title" style={{ width: "32%" }} />
            <span className="skel skel--label" />
          </div>
        </div>
        <div className="adm__overview" aria-hidden="true">
          <div className="adm__meta">
            {[0, 1, 2].map((slot) => (
              <div key={slot} className="adm__metaitem">
                <span className="skel skel--label" />
                <span className="skel" style={{ width: "48%", height: 16, marginTop: 8 }} />
              </div>
            ))}
          </div>
          <div className="adm__review">
            <span className="skel skel--label" />
            <span className="skel skel--line" />
            <span className="skel skel--line" />
          </div>
        </div>
        <div className="adm__pairs" aria-hidden="true">
          <span className="skel skel--line" />
          <span className="skel skel--line" />
          <span className="skel skel--line" />
        </div>
      </section>
    );
  }

  const payload: Record<string, string> = data.payload ?? {};
  const origins: Record<string, string> = data.origins ?? {};
  const labels: Record<string, string> = data.labels ?? {};
  // `*_candidate` przychodzi tylko wtedy, gdy kandydat wypełniał w innym języku
  // niż polski — panel mówi po polsku, ale rozmowa toczy się o jego ekran.
  const answered: AnsweredField[] = data.answered ?? [];
  const files: StoredFileRow[] = data.files ?? [];

  return (
    <section className="adm__section">
      <div className="adm__head">
        <button className="btn btn--ghost" onClick={onBack}>
          ‹ Wróć do listy
        </button>
        <div className="adm__headmain">
          <h2 className="adm__title">
            {[payload.imie, payload.nazwisko].filter(Boolean).join(" ") || "Kandydat bez danych"}
          </h2>
          <p className="adm__muted">
            <span className="adm__mono" data-ybug-sensitive>{data.phone}</span>{" "}
            <button
              className="icon-btn adm__fix"
              title="Zmień numer telefonu (kody SMS i link idą na ten numer)"
              disabled={busy}
              onClick={zmienTelefon}
            >
              ✎
            </button>{" "}
            · {data.record_id}
            {/* Rozmowa ze zlecającym toczy się o jego numer sprawy — ma być
                widoczny obok naszego, a nie tylko w bazie. */}
            {data.external_id && <> · sprawa <span className="adm__mono">{data.external_id}</span></>}
            {/* Język, w którym kandydat wypełnia proces. Konsultant dzwoni do
                człowieka i musi wiedzieć, w jakim języku zacząć — a karta jest
                po polsku niezależnie od tego, co widzi kandydat. */}
            {data.candidate_locale && (
              <> · język: <strong>{JEZYK[data.candidate_locale] ?? data.candidate_locale}</strong></>
            )}
          </p>
          {/* Wydruk i link to narzędzia do sprawy, nie decyzje o niej. Stały
              w jednym rzędzie z „zakończ odmową" i ciągnęły wzrok tak samo
              mocno jak akcje, których nie da się cofnąć. */}
          <p className="adm__tools">
            <button className="btn--link" onClick={() =>
              setPreview({
                path: admin.dossierUrl(id),
                title: "Karta kandydata",
                filename: `karta-${payload.nazwisko ?? id.slice(0, 8)}.pdf`,
              })
            }>
              Karta PDF
            </button>
            <button className="btn--link" onClick={() => navigator.clipboard?.writeText(data.magic_link)}>
              Kopiuj link dla kandydata
            </button>
            {/* Adres tej karty — to on ląduje w powiadomieniu dla konsultanta. */}
            <button className="btn--link" onClick={() =>
              navigator.clipboard?.writeText(`${window.location.origin}/admin/sprawa/${id}`)
            }>
              Kopiuj link do karty
            </button>
          </p>
        </div>
        <div className="adm__actions">
          {/* Bez zdjęcia flagi kolejka „proszą o pomoc" po tygodniu przestaje
              cokolwiek znaczyć — wszyscy w niej zostają na zawsze. */}
          {data.needs_help && (
            <button
              className="btn btn--primary"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  await admin.resolveHelp(id);
                  await load();
                } catch (caught) {
                  setError((caught as AdminError).message);
                } finally {
                  setBusy(false);
                }
              }}
            >
              Zgłoszenie obsłużone
            </button>
          )}
          {/* O akcjach decyduje stan sprawy, nie stan algorytmu. Rekord, który
              doszedł do blokady, jest wciąż otwarty — a panel czytał „blocked"
              jako „zamknięty" i proponował otwarcie czegoś, co i tak jest
              otwarte, chowając przy tym jedyne sensowne tu akcje. */}
          {(data.record_status ?? data.status) === "open" ? (
            <>
              <button className="btn btn--secondary" disabled={busy} onClick={() => act("remind")}>
                Przypomnij
              </button>
              {/* Akcje niszczące mówią maliną w samym tekście (adm__risky) —
                  pełne wypełnienie znaczy w panelu „akcja główna", więc groźba
                  nie może wyglądać jak zaproszenie. */}
              <button className="btn btn--secondary adm__risky" disabled={busy} onClick={() => act("reset")}>
                Wyczyść i od nowa
              </button>
              <button className="btn btn--secondary adm__risky" disabled={busy} onClick={() => act("failed")}>
                Zakończ odmową
              </button>
              <button className="btn btn--primary" disabled={busy} onClick={() => act("success")}>
                Zakończ sukcesem
              </button>
            </>
          ) : (
            // Zamknięty onboarding jest zamknięty na zawsze — nie ma tu akcji.
            <span className="adm__muted">Onboarding zamknięty</span>
          )}
        </div>
      </div>

      {/* Strefa przeglądu: fakty sprawy po lewej, trzy osie stanu po prawej —
          obok siebie zamiast jedna pod drugą, żeby teczka zaczynała się nad
          zgięciem ekranu. Fakty czyta się, osie się przestawia: to dwie różne
          czynności i dwie różne karty. */}
      <div className="adm__overview">
        <div className="adm__meta">
          <Meta label="Proces" value={data.master_label ?? MASTER_LABEL[data.master] ?? data.status} />
          {/* Pytanie, na którym proces stoi — najważniejszy fakt teczki,
              więc stoi na drugim miejscu, nie na czwartym. Klucz w mono,
              bo to identyfikator porównywany wzrokiem ze schematem. */}
          <div className="adm__metaitem">
            <span className="adm__metalabel">Bieżące pytanie</span>
            <span className="adm__metavalue adm__metavalue--mono">{data.current?.key ?? "—"}</span>
          </div>
          <div className="adm__metaitem">
            <span className="adm__metalabel">Postęp</span>
            <span className="adm__metavalue">{data.progress}%</span>
            {/* Liczba niesie wartość, pasek kształt — od progu widać,
                czy sprawa raczkuje, czy dojeżdża do końca. */}
            <span className="meter meter--thin" aria-hidden="true">
              <span style={{ width: `${data.progress}%` }} />
            </span>
          </div>
          <Meta label="Odpowiedzi" value={String(Object.keys(payload).length)} />
          {/* Flagi ustawione przez drzewo warunków (typ „set flag") — te same,
              które czyta API w bloku `flags`. */}
          {Object.keys(data.flags ?? {}).length > 0 && (
            <div className="adm__metaitem" data-testid="case-flags">
              <span className="adm__metalabel">Flagi</span>
              <span className="adm__metavalue">
                {Object.entries(data.flags as Record<string, string>).map(([id, value]) => (
                  <span key={id} className="pill pill--info" title={id}>{id}: {value}</span>
                ))}
              </span>
            </div>
          )}
          {/* Sama data nie mówi, czy sprawa stoi od godziny, czy od trzech tygodni. */}
          <div className="adm__metaitem">
            <span className="adm__metalabel">Trwa</span>
            <span className="adm__metavalue">
              <Stoper od={data.created_at} do={data.closed_at} />
            </span>
          </div>
          <Meta label="Utworzony" value={formatDate(data.created_at)} />
        </div>

        {/* Dwie osie, każda odpowiada na inne pytanie. Stoją osobno od przycisków
            akcji, bo to nie akcje, tylko stan sprawy — a mieszane w jednym rzędzie
            dawały zdania bez sensu („zakończona", a czeka na uzupełnienie). */}
        <div className="adm__review">
          <span className="adm__fieldlabel">
            Pomoc — {HELP_LABEL[data.help_status] ?? "nie jest potrzebna"}
          </span>
          {data.help_comment && (
            <p className="adm__helpkomentarz" data-testid="help-comment" data-ybug-sensitive>
              {data.help_comment}
            </p>
          )}
          <div className="dep__list">
            {HELP_STATES.map((state) => (
              <button
                key={state.id}
                type="button"
                className={`dep__chip${data.help_status === state.id ? " dep__chip--on" : ""}`}
                disabled={busy}
                onClick={() => setHelp(state.id)}
              >
                {state.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <nav className="adm__tabs">
        {(["dane", "algorytm", "pliki", "historia"] as const).map((name) => (
          <button
            key={name}
            className={`adm__tab${tab === name ? " adm__tab--on" : ""}`}
            onClick={() => setTab(name)}
          >
            {{
              dane: "Dane",
              algorytm: "Algorytm",
              pliki: `Pliki (${files.length})`,
              historia: `Historia (${audit.length})`,
            }[name]}
          </button>
        ))}
      </nav>

      {tab === "dane" && (
        <dl className="adm__pairs">
          {/* Pytanie, na którym proces stoi — na SZCZYCIE teczki, nie na dnie.
              To jedyny wiersz, z którym można coś zrobić od ręki (odpowiedzieć
              za kandydata, pominąć), a na dole listy trzydziestu odpowiedzi
              bywał pod zgięciem ekranu — konsultant widział przeszłość,
              a nie to, na czym sprawa właśnie stoi. */}
          {data.current?.key && (
            <div className="adm__pair adm__pair--current">
              <dt>
                {data.current.title ?? data.current.key}
                <span className="adm__key">{data.current.key}</span>
              </dt>
              <dd>
                <span className="adm__muted">czeka na odpowiedź</span>
                <button
                  className="icon-btn adm__fix"
                  title="Odpowiedz za kandydata"
                  onClick={() => setFixing(data.current.key)}
                >
                  ✎
                </button>
                {/* Pominięcie dotyczy właśnie tego pytania, więc stoi przy nim,
                    a nie w pasku akcji nad całą sprawą. */}
                <button
                  className="icon-btn adm__fix"
                  title="Pomiń to pytanie"
                  disabled={busy}
                  onClick={() => act("skip")}
                >
                  ⤼
                </button>
              </dd>
            </div>
          )}
          {answered.map((field) => (
            <div key={field.key} className="adm__pair">
              <dt>
                {field.title}
                {/* To samo pytanie w języku kandydata: przez telefon trzeba
                    powiedzieć dokładnie to, co on widział na ekranie. */}
                {field.title_candidate && field.title_candidate !== field.title && (
                  <span className="adm__foreign">{field.title_candidate}</span>
                )}
                <span className="adm__key">{field.key}</span>
              </dt>
              <dd {...sensitive(field)}>
                {field.display || labels[`${field.key}:${payload[field.key]}`] || payload[field.key]}
                {field.display_candidate && field.display_candidate !== field.display && (
                  <span className="adm__foreign">{field.display_candidate}</span>
                )}
                {field.source ? (
                  <span className={`badge badge--${field.source}`}>
                    {SOURCE_BADGE[field.source] ?? field.source}
                  </span>
                ) : origins[field.key] && origins[field.key] !== "user" ? (
                  <span className={`badge badge--${origins[field.key]}`}>
                    {ORIGIN_BADGE[origins[field.key]] ?? origins[field.key]}
                  </span>
                ) : null}
                {/* Odczyt z OCR — podgląd pliku źródłowego, żeby zweryfikować,
                    czy model przeczytał dobrze, bez szukania po zakładce Pliki. */}
                {field.ocr_source && (() => {
                  const zrodlo = files.find((f) => f.key === field.ocr_source);
                  return zrodlo ? (
                    <button
                      className="icon-btn adm__fix"
                      title="Podgląd pliku, z którego OCR odczytał tę wartość"
                      onClick={() =>
                        setPreview({
                          path: admin.fileUrl(id, zrodlo.id),
                          title: zrodlo.filename,
                          filename: zrodlo.filename,
                        })
                      }
                    >
                      🔍
                    </button>
                  ) : null;
                })()}
                {/* Odczyt z dokumentu ma wyższą rangę niż wpis kandydata, więc
                    literówka OCR-a była nie do ruszenia z żadnej strony. */}
                <button
                  className="icon-btn adm__fix"
                  title="Popraw odpowiedź"
                  onClick={() => setFixing(field.key)}
                >
                  ✎
                </button>
                {/* Skasowanie to osobna intencja niż poprawka i zasługuje na
                    własny przycisk: konsultant, który chce tylko cofnąć błędną
                    odpowiedź, nie powinien przechodzić przez okno edycji.
                    Powód wymagany tak samo — skasowanie cudzej odpowiedzi bez
                    śladu jest nie do odróżnienia od utraty danych. */}
                <button
                  className="icon-btn adm__fix adm__risky"
                  title="Skasuj odpowiedź — proces zapyta o nią ponownie"
                  disabled={busy}
                  onClick={() => czysc(field.key, field.title)}
                >
                  ✕
                </button>
                {/* Pytanie iDenfy: co odczytał vendor (i do którego pola trafiło)
                    oraz pliki dokumentu ze skanu — w samym pytaniu, nie w osobnym. */}
                {field.values && field.values.length > 0 && (
                  <ul className="adm__kycvalues" data-testid="kyc-values">
                    {field.values.map((v) =>
                      v.kind === "reading" ? (
                        <li key={`r-${v.field}`} {...sensitive({ key: v.target ?? v.field })}>
                          <span className="adm__muted">{v.label}:</span> {v.value}
                          {v.target && <span className="adm__key">→ {v.target}</span>}
                        </li>
                      ) : (
                        <li key={`f-${v.file_id}`}>
                          <button
                            className="btn--link"
                            data-ybug-sensitive
                            onClick={() =>
                              setPreview({
                                path: admin.fileUrl(id, v.file_id),
                                title: v.filename,
                                filename: v.filename,
                              })
                            }
                          >
                            📎 {v.filename}
                          </button>
                        </li>
                      ),
                    )}
                  </ul>
                )}
              </dd>
            </div>
          ))}
          {answered.length === 0 && !data.current?.key && (
            <p className="adm__muted">Kandydat nie odpowiedział jeszcze na żadne pytanie.</p>
          )}
        </dl>
      )}

      {fixing && (
        <Override
          recordId={id}
          // Pytanie bieżące nie jest jeszcze w odpowiedziach — jego opis bierzemy
          // z tego, co proces właśnie pokazuje kandydatowi.
          field={
            answered.find((item) => item.key === fixing) ??
            (data.current?.key === fixing ? (data.current as AnsweredField) : undefined)
          }
          current={payload[fixing]}
          onClose={() => setFixing(null)}
          onSaved={async () => {
            setFixing(null);
            await load();
          }}
        />
      )}

      {tab === "algorytm" && (
        <div className="adm__algo">
          <ol className="adm__steps">
            {(data.trace ?? []).map((step: any) => (
              <li key={step.n}>
                <span className="adm__stepno">{step.n}</span>
                <span>{step.label}</span>
                {step.dropped > 0 && <span className="adm__drop">−{step.dropped}</span>}
                <span className="adm__mono">{step.value}</span>
              </li>
            ))}
          </ol>
          <h3 className="adm__subtitle">Warunek bieżącego pytania</h3>
          <pre className="adm__code">{JSON.stringify(data.conditions ?? null, null, 2)}</pre>
          <h3 className="adm__subtitle">Kolejka ({data.queue_size})</h3>
          <ul className="adm__queue">
            {(data.queue ?? []).map((item: any) => (
              <li key={item.key}>
                <span className="adm__mono">{item.index}</span> {item.key}
              </li>
            ))}
          </ul>
        </div>
      )}

      {tab === "pliki" && (
        <div className="adm__files">
          {groupFiles(files).map((group) => (
            <FileTile
              key={group.key}
              group={group}
              onOpen={(file) =>
                setPreview({
                  path: admin.fileUrl(id, file.id),
                  title: file.filename,
                  filename: file.filename,
                })
              }
            />
          ))}
          {files.length === 0 && <p className="adm__muted">Brak wgranych plików.</p>}
        </div>
      )}

      {preview && <FilePreview {...preview} onClose={() => setPreview(null)} />}

      {tab === "historia" && (
        <ul className="adm__log">
          {audit.map((event, position) => (
            <li key={`${event.ts}-${position}`}>
              <span className="adm__muted adm__mono">{formatDate(event.ts, true)}</span>
              <span className={`adm__action adm__action--${event.action}`}>{event.action}</span>
              <span className="adm__mono">{event.field}</span>
              {/* Bez tego historia mówiła, co się stało, ale nie czyja to była
                  decyzja — przy poprawianiu cudzych odpowiedzi to sedno śladu.
                  Sam adres nie wystarcza: przy wejściu kluczem stało tam „panel",
                  czyli nazwa naszego klucza, a nie odpowiedź na pytanie „kto". */}
              <span className={`pill pill--src-${event.source ?? "system"}`}>
                {SOURCE_LABEL[event.source ?? "system"] ?? event.source}
              </span>
              {event.actor && event.actor !== "panel" && (
                <span className="adm__tag">{event.actor}</span>
              )}
              <span className="adm__muted">{event.detail}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * Poprawka odpowiedzi ręką konsultanta.
 *
 * Odczyt z dokumentu stoi wyżej niż wpis kandydata — i to jest w porządku,
 * dopóki OCR czyta dobrze. Gdy przeczyta „O" zamiast „0", literówka zostawała
 * w teczce na zawsze: kandydat nie mógł jej nadpisać, bo pole było zajęte
 * wartością wyższej rangi. Wpis konsultanta stoi nad odczytem, ale nie udaje
 * odczytu — dostaje własne oznaczenie, a w dzienniku ląduje nazwisko i powód.
 *
 * Powód jest wymagany: poprawka bez niego to zmiana danych kandydata, po której
 * zostaje sama nowa wartość — nie do odróżnienia od cichej podmiany.
 */
function Override({
  recordId,
  field,
  current,
  onClose,
  onSaved,
}: {
  recordId: string;
  field?: AnsweredField;
  current: string | undefined;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [value, setValue] = useState(current ?? "");
  const [comment, setComment] = useState("");
  const [notApplicable, setNotApplicable] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useScrollLock();

  if (!field) return null;

  // Podpisu ani skanu nie przepisze się w polu tekstowym. Dla nich poprawką jest
  // nowy plik albo skasowanie odpowiedzi, żeby kandydat zrobił to jeszcze raz —
  // dotąd konsultant nie miał z takim polem co zrobić.
  const plikowe = field.type === "file" || field.type === "idenfy";
  const podpis = field.type === "simple_sign";
  const wyborem = Boolean(field.options?.length);

  const zapisz = async (dzialanie: "value" | "file" | "clear") => {
    setBusy(true);
    setError(null);
    try {
      let wynik: { cleared: string[] };
      if (dzialanie === "clear") {
        wynik = await admin.clearAnswer(recordId, field.key, comment);
      } else if (dzialanie === "file" && files.length) {
        // Pytanie wieloplikowe (paszport, legitymacja) przyjmuje komplet — każdy
        // plik idzie osobnym żądaniem, backend dokłada je pod jednym pytaniem.
        let ostatni: { cleared: string[] } | undefined;
        for (const plik of files) {
          ostatni = await admin.attachFile(recordId, field.key, comment, plik);
        }
        wynik = ostatni!;
      } else {
        wynik = await admin.override(recordId, {
          key: field.key,
          value: notApplicable ? null : value,
          comment,
        });
      }
      // Zmiana u korzenia gałęzi kasuje wszystko, co z niej wynikało —
      // konsultant ma to zobaczyć od razu, a nie odkryć później w teczce.
      if (wynik.cleared?.length) {
        window.alert(
          `Zmiana wyczyściła ${wynik.cleared.length} zależnych odpowiedzi:\n` +
            wynik.cleared.join(", "),
        );
      }
      onSaved();
    } catch (caught) {
      setError((caught as AdminError).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal" role="dialog" aria-modal="true" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal__card">
        <div className="modal__head">
          <h2 className="modal__title">Popraw: {field.title}</h2>
          <button className="btn btn--ghost" onClick={onClose}>
            Zamknij
          </button>
        </div>

        <div className="modal__body">
          <p className="adm__muted">
            Teraz: <span className="adm__mono">{String(field.display || current || "—")}</span>
          </p>

          {podpis ? (
            <p className="adm__muted">
              Podpisu nie da się przepisać w polu tekstowym. Albo wgrywasz podpisany
              dokument (skan z biura), albo kasujesz odpowiedź i kandydat podpisuje
              jeszcze raz.
            </p>
          ) : plikowe ? (
            <p className="adm__muted">
              To pole wypełnia się plikiem. Wgraj dokument albo skasuj odpowiedź, żeby
              kandydat wgrał go sam.
            </p>
          ) : wyborem ? (
            /* Warianty zamiast wolnego pola: wartość wpisana z palca kończyła się
               samym „Błąd 422", bez podpowiedzi, co wolno wybrać. */
            <label className="adm__field">
              <span className="adm__fieldlabel">Nowa wartość</span>
              <select
                className="select"
                autoFocus
                disabled={notApplicable}
                value={value}
                onChange={(event) => setValue(event.target.value)}
              >
                <option value="">— wybierz —</option>
                {field.options!.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <label className="adm__field">
              <span className="adm__fieldlabel">Nowa wartość</span>
              <input
                className="input"
                autoFocus
                disabled={notApplicable}
                type={field.type === "date" ? "date" : field.type === "number" ? "number" : "text"}
                placeholder={field.placeholder ?? undefined}
                value={value}
                onChange={(event) => setValue(event.target.value)}
              />
              {/* Te same reguły, które widzi kandydat — inaczej konsultant poznaje
                  je dopiero po odmowie zapisu. */}
              {(field.hint || field.validation?.mask || field.validation?.checksum) && (
                <span className="adm__muted">
                  {field.hint}
                  {field.validation?.mask ? ` · format: ${field.validation.mask}` : ""}
                  {field.validation?.checksum ? ` · suma kontrolna: ${field.validation.checksum}` : ""}
                </span>
              )}
            </label>
          )}

          {(plikowe || podpis) && (
            <label className="adm__field">
              <span className="adm__fieldlabel">Plik</span>
              <input
                className="input"
                type="file"
                // Skan wielostronicowy (paszport, obie strony legitymacji) — konsultant
                // wybiera wszystkie strony naraz. Podpis to jeden dokument.
                multiple={field.type === "file"}
                onChange={(event) => setFiles(Array.from(event.target.files ?? []))}
              />
              {files.length > 1 && (
                <span className="adm__muted">Wybrano plików: {files.length}</span>
              )}
            </label>
          )}

          {/* Bywa, że pola naprawdę nie da się wypełnić — kandydat bez PESEL-u
              utykał na pytaniu, na które nie ma odpowiedzi. */}
          {!plikowe && !podpis && (
            <label className="adm__check">
              <input
                type="checkbox"
                checked={notApplicable}
                onChange={(event) => setNotApplicable(event.target.checked)}
              />
              To pytanie tej osoby nie dotyczy
            </label>
          )}

          <label className="adm__field">
            <span className="adm__fieldlabel">Powód poprawki (trafia do dziennika)</span>
            <input
              className="input"
              placeholder="np. OCR odczytał z błędem, sprawdzone na skanie"
              value={comment}
              onChange={(event) => setComment(event.target.value)}
            />
          </label>

          {error && <div className="adm__alert adm__alert--error">{error}</div>}
        </div>

        <div className="modal__head adm__footer">
          {/* Skasowanie zostawia pytanie bez odpowiedzi — proces zapyta o nie
              jeszcze raz. Przy podpisie i skanie to jedyne uczciwe wyjście. */}
          <button
            className="btn btn--ghost adm__risky"
            disabled={busy || !comment.trim()}
            onClick={() => {
              if (window.confirm("Skasować odpowiedź? Proces zapyta o nią ponownie.")) {
                void zapisz("clear");
              }
            }}
          >
            Skasuj odpowiedź
          </button>
          <span className="adm__spacer" />
          <button className="btn btn--secondary" onClick={onClose}>
            Anuluj
          </button>
          {plikowe || podpis ? (
            <button
              className="btn btn--primary"
              disabled={busy || !comment.trim() || !files.length}
              onClick={() => zapisz("file")}
            >
              {busy ? "Wgrywam…" : files.length > 1 ? `Wgraj pliki (${files.length})` : "Wgraj plik"}
            </button>
          ) : (
            <button
              className="btn btn--primary"
              disabled={busy || !comment.trim() || (!notApplicable && !value.trim())}
              onClick={() => zapisz("value")}
            >
              {busy ? "Zapisuję…" : "Zapisz poprawkę"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Ile czasu minęło od startu sprawy.
 *
 * Sama data mówi „11.08, 23:14" i nic z tego nie wynika — konsultant liczył
 * w głowie, ile to dni. Sprawa sprzed trzech godzin i sprzed trzech tygodni
 * wymagają zupełnie innej rozmowy, a różnicy nie widać na pierwszy rzut oka.
 *
 * Przy sprawie zamkniętej stoper się zatrzymuje: liczy się, ile trwała, a nie
 * ile czasu minęło od jej domknięcia. Krok zegara dobieramy do skali — sekundy
 * mają sens w pierwszej godzinie, przy tygodniach są szumem.
 */
function Stoper({ od, do: koniec }: { od?: string | null; do?: string | null }) {
  const [teraz, setTeraz] = useState(() => Date.now());

  const zamkniete = koniec ? new Date(koniec).getTime() : null;
  const start = od ? new Date(od).getTime() : null;

  useEffect(() => {
    if (!start || zamkniete) return;
    const minelo = Date.now() - start;
    // W pierwszej godzinie tyka co sekundę, dalej co minutę: przy sprawie
    // sprzed tygodnia odświeżanie co sekundę to sam ruch bez informacji.
    const krok = minelo < 3600_000 ? 1000 : 60_000;
    const timer = window.setInterval(() => setTeraz(Date.now()), krok);
    return () => window.clearInterval(timer);
  }, [start, zamkniete]);

  if (!start) return <>—</>;

  const sekundy = Math.max(0, Math.floor(((zamkniete ?? teraz) - start) / 1000));
  return (
    <>
      {trwanie(sekundy)}
      {zamkniete && <span className="adm__muted"> (zamknięta)</span>}
    </>
  );
}

/** Czas trwania po ludzku: dwie największe jednostki wystarczą. */
function trwanie(sekundy: number): string {
  const dni = Math.floor(sekundy / 86400);
  const godziny = Math.floor((sekundy % 86400) / 3600);
  const minuty = Math.floor((sekundy % 3600) / 60);
  const reszta = sekundy % 60;

  if (dni) return `${dni} dn. ${godziny} godz.`;
  if (godziny) return `${godziny} godz. ${minuty} min`;
  if (minuty) return `${minuty} min ${reszta} s`;
  return `${reszta} s`;
}


function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div className="adm__metaitem">
      <span className="adm__metalabel">{label}</span>
      <span className="adm__metavalue">{value}</span>
    </div>
  );
}

/**
 * Jeden format daty w całym panelu: `21.08, 14:32` — rok dopiero wtedy, gdy
 * jest inny niż bieżący. Konsultant porównuje daty wzrokiem dziesiątki razy
 * dziennie: stały szablon o stałej długości robi z kolumny dat pion do
 * skanowania, a rok, który zawsze jest ten sam, był tylko szumem łamiącym
 * komórki na dwie linie. Drugi argument został w podpisie, bo format jest
 * teraz jeden — wywołania z `true` niczego już nie przełączają.
 */
export function formatDate(value: string | null, _withTime = false): string {
  if (!value) return "—";
  const date = new Date(value);
  const rok = date.getFullYear() === new Date().getFullYear() ? "" : `.${date.getFullYear()}`;
  const dzien = date.toLocaleDateString("pl-PL", { day: "2-digit", month: "2-digit" });
  const czas = date.toLocaleTimeString("pl-PL", { hour: "2-digit", minute: "2-digit" });
  return `${dzien}${rok}, ${czas}`;
}

/** Pliki jednego pytania — jeden kafelek na datapoint, w kolejności wgrania. */
interface FileGroup {
  key: string;
  title: string;
  files: StoredFileRow[];
}

function groupFiles(files: StoredFileRow[]): FileGroup[] {
  const groups = new Map<string, FileGroup>();
  for (const file of files) {
    const group = groups.get(file.key) ?? { key: file.key, title: file.title || file.key, files: [] };
    group.files.push(file);
    groups.set(file.key, group);
  }
  return [...groups.values()];
}

/**
 * Kafelek pytania z jego plikami. Kilka plików (strony paszportu, przód i tył
 * dokumentu) przewija się strzałkami w jednym kafelku — wcześniej każdy plik
 * był osobną kartą i trzeba było zgadywać, do którego pytania należy.
 */
function FileTile({ group, onOpen }: { group: FileGroup; onOpen: (file: StoredFileRow) => void }) {
  const [at, setAt] = useState(0);
  const total = group.files.length;
  // Lista bywa odświeżana (plik usunięty/dołożony) — indeks nie może wyjść poza nią.
  const file = group.files[Math.min(at, total - 1)];

  return (
    <div className="adm__tile" data-testid="file-tile">
      <div className="adm__tilehead">{group.title}</div>
      <button className="adm__file" onClick={() => onOpen(file)} data-ybug-sensitive>
        <span className="adm__filename">{file.filename}</span>
        <span className="adm__muted">
          {/* Rozmowa toczy się o numer z wydruku, więc po nim trzeba
              trafić w plik. Skany od kandydata numeru nie mają. */}
          {file.doc_number && <span className="adm__mono">{file.doc_number} · </span>}
          {file.key} · {file.size_kb} kB
        </span>
      </button>
      {total > 1 && (
        <div className="adm__carousel">
          <button
            className="btn btn--ghost"
            aria-label="Poprzedni plik"
            disabled={at <= 0}
            onClick={() => setAt(at - 1)}
          >
            ‹
          </button>
          <span className="adm__carouselpos">{Math.min(at, total - 1) + 1} / {total}</span>
          <button
            className="btn btn--ghost"
            aria-label="Następny plik"
            disabled={at >= total - 1}
            onClick={() => setAt(at + 1)}
          >
            ›
          </button>
        </div>
      )}
    </div>
  );
}
